import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../hooks/useSEO';

const PageContainer = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 2rem;
  line-height: 1.6;
  color: var(--text-primary);
  background: var(--bg-primary);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: linear-gradient(135deg, var(--brand-red) 0%, var(--brand-red-dark) 100%);
  color: white;
  border-radius: 12px;
  margin: -2rem -2rem 2rem -2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.75rem;
  color: white;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  opacity: 0.95;
  margin: 0;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: var(--brand-red);
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  border-bottom: 2px solid var(--brand-blue);
  padding-bottom: 0.35rem;
`;

const P = styled.p`
  margin-bottom: 0.75rem;
`;

const List = styled.ul`
  margin: 0.5rem 0 0.75rem;
  padding-left: 1.5rem;
`;

const Li = styled.li`
  margin-bottom: 0.35rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 400;
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 400;
  min-height: 100px;
  resize: vertical;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  font-weight: 400;
  cursor: pointer;

  input {
    margin-top: 0.25rem;
    flex-shrink: 0;
  }
`;

const SubmitButton = styled.button`
  align-self: flex-start;
  background: linear-gradient(135deg, var(--brand-red) 0%, var(--brand-red-dark) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.85rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin: 0;
  background: ${(p) => (p.$error ? '#fef2f2' : '#ecfdf5')};
  color: ${(p) => (p.$error ? '#991b1b' : '#065f46')};
  border: 1px solid ${(p) => (p.$error ? '#fecaca' : '#a7f3d0')};
`;

const BackRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
`;

const BackLink = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, var(--brand-red) 0%, var(--brand-red-dark) 100%);
  color: white;
  padding: 0.8rem 1.5rem;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
`;

const BackLinkSecondary = styled(Link)`
  display: inline-block;
  background: transparent;
  color: var(--brand-red);
  padding: 0.8rem 1.5rem;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  border: 2px solid var(--brand-red);
`;

const AccountDeletionRequestPage = () => {
  const { t } = useTranslation();
  const ns = 'accountDeletionRequest';

  useSEO({
    title: t(`${ns}.page.seo.title`),
    description: t(`${ns}.page.seo.description`),
    keywords: t(`${ns}.page.seo.keywords`),
    canonical: 'https://helveticpadelseries.ch/account-deletion-request',
  });

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    acknowledgeRetention: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');
    setSubmitError(false);

    if (!form.fullName.trim() || !form.email.trim() || !form.phone.trim() || !form.acknowledgeRetention) {
      setSubmitMessage(t(`${ns}.form.errorRequired`));
      setSubmitError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/account-deletion-request/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();

      if (data.success) {
        setSubmitMessage(data.message || t(`${ns}.form.success`));
        setSubmitError(false);
        setForm({
          fullName: '',
          email: '',
          phone: '',
          message: '',
          acknowledgeRetention: false,
        });
      } else {
        setSubmitMessage(data.message || t(`${ns}.form.errorGeneric`));
        setSubmitError(true);
      }
    } catch {
      setSubmitMessage(t(`${ns}.form.errorGeneric`));
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteItems = t(`${ns}.whatWeDelete.items`, { returnObjects: true });
  const retainItems = t(`${ns}.whatWeRetain.items`, { returnObjects: true });
  const processItems = t(`${ns}.process.items`, { returnObjects: true });

  return (
    <PageContainer>
      <Header>
        <Title>{t(`${ns}.page.headerTitle`)}</Title>
        <Subtitle>{t(`${ns}.page.headerSubtitle`)}</Subtitle>
      </Header>

      <Section>
        <SectionTitle>{t(`${ns}.intro.title`)}</SectionTitle>
        <P>{t(`${ns}.intro.p1`)}</P>
        <P>{t(`${ns}.intro.p2`)}</P>
      </Section>

      <Section>
        <SectionTitle>{t(`${ns}.whatWeDelete.title`)}</SectionTitle>
        <List>
          {Array.isArray(deleteItems) && deleteItems.map((item) => (
            <Li key={item}>{item}</Li>
          ))}
        </List>
      </Section>

      <Section>
        <SectionTitle>{t(`${ns}.whatWeRetain.title`)}</SectionTitle>
        <P>{t(`${ns}.whatWeRetain.intro`)}</P>
        <List>
          {Array.isArray(retainItems) && retainItems.map((item) => (
            <Li key={item}>{item}</Li>
          ))}
        </List>
        <P>{t(`${ns}.whatWeRetain.outro`)}</P>
      </Section>

      <Section>
        <SectionTitle>{t(`${ns}.process.title`)}</SectionTitle>
        <List>
          {Array.isArray(processItems) && processItems.map((item) => (
            <Li key={item}>{item}</Li>
          ))}
        </List>
      </Section>

      <Section>
        <SectionTitle>{t(`${ns}.form.title`)}</SectionTitle>
        <Form onSubmit={handleSubmit}>
          <Label>
            {t(`${ns}.form.fullNameLabel`)}
            <Input
              name="fullName"
              value={form.fullName}
              onChange={handleChange}
              placeholder={t(`${ns}.form.fullNamePlaceholder`)}
              required
              autoComplete="name"
            />
          </Label>

          <Label>
            {t(`${ns}.form.emailLabel`)}
            <Input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder={t(`${ns}.form.emailPlaceholder`)}
              required
              autoComplete="email"
            />
          </Label>

          <Label>
            {t(`${ns}.form.phoneLabel`)}
            <Input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder={t(`${ns}.form.phonePlaceholder`)}
              required
              autoComplete="tel"
            />
          </Label>

          <Label>
            {t(`${ns}.form.messageLabel`)}
            <TextArea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder={t(`${ns}.form.messagePlaceholder`)}
            />
          </Label>

          <CheckboxRow>
            <input
              type="checkbox"
              name="acknowledgeRetention"
              checked={form.acknowledgeRetention}
              onChange={handleChange}
            />
            <span>{t(`${ns}.form.acknowledgeLabel`)}</span>
          </CheckboxRow>

          {submitMessage ? <Message $error={submitError}>{submitMessage}</Message> : null}

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? t(`${ns}.form.submitting`) : t(`${ns}.form.submit`)}
          </SubmitButton>

          <P>{t(`${ns}.form.privacyNote`)}</P>
        </Form>
      </Section>

      <BackRow>
        <BackLink to="/">{t(`${ns}.page.backToHome`)}</BackLink>
        <BackLinkSecondary to="/privacy-policy">{t(`${ns}.page.backToPrivacy`)}</BackLinkSecondary>
      </BackRow>
    </PageContainer>
  );
};

export default AccountDeletionRequestPage;
